import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { SearchBannerGQL } from 'src/app/pages/search-results/list/search-banner/search-banner.query.generated';
import { Key } from 'src/app/common/utils/helpers';
import {
  centerOfMunicipalities,
  centerOfProvinces,
  centerOfRegions,
  municipalities,
  places,
  regions,
} from 'utils/places-utility';
import { SearchResultsFilterablesService } from 'utils/search-results-filterable';
import { CategoryByNormalizedNameGQL } from './search-results.query.generated';

@Injectable({
  providedIn: 'root',
})
export class SearchEngineResolver {
  constructor(
    private categoryByNormalizedNameGQL: CategoryByNormalizedNameGQL,
    private searchBannerGQL: SearchBannerGQL,
    public searchResultsFilterablesService: SearchResultsFilterablesService,
  ) {}

  resolve(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<Params> {
    console.log('[Resolver] route.params:', route.params);
    console.log('[Resolver] route.queryParams:', route.queryParams);

    if (route.params?.category) {
      return this.categoryByNormalizedNameGQL.fetch({ name: route.params.category }).pipe(
        map(r => {
          let searchCategories: string[] = [];
          const cat = r.data.categoryByNormalizedName.id;
          if (!route.queryParams.searchCategories) {
            searchCategories = [cat];
          } else if (
            typeof route.queryParams.searchCategories === 'string' &&
            route.queryParams.searchCategories !== cat
          ) {
            searchCategories = [cat, route.queryParams.searchCategories];
          } else if (!route.queryParams.searchCategories.includes(cat)) {
            searchCategories = [cat, ...route.queryParams.searchCategories];
          }
          const types = (route.data.types || [])
            .map(
              (type: string) =>
                this.searchResultsFilterablesService[(type + 'FilterableIds') as Key<SearchResultsFilterablesService>],
            )
            .flat();
          return { searchCategories, types };
        }),
      );
    } else if (route.params?.location) {
      const additionals: Params = {};
      if (route.data?.isRegion) {
        additionals.value = regions[route.params.location];
        additionals.region = regions[route.params.location];
        const center = centerOfRegions.find(c => c.value === additionals.value);
        additionals.latitude = center?.latitude;
        additionals.longitude = center?.longitude;
      }
      if (route.data?.isProvince) {
        additionals.value = places[route.params.location];
        additionals.province = places[route.params.location];
        const center = centerOfProvinces.find(c => c.value === additionals.value);
        additionals.latitude = center?.latitude;
        additionals.longitude = center?.longitude;
      }
      if (route.data?.isMunicipality) {
        additionals.value = municipalities[route.params.location];
        additionals.municipality = municipalities[route.params.location];
        additionals.distance = 50;
        const center = centerOfMunicipalities.find(c => c.value === additionals.value);
        additionals.latitude = center?.latitude;
        additionals.longitude = center?.longitude;
      }
      additionals.types = (route.data.types || [])
        .map(
          (type: string) =>
            this.searchResultsFilterablesService[(type + 'FilterableIds') as Key<SearchResultsFilterablesService>],
        )
        .flat();
      additionals.banner = route.params.location;
      return of({ ...additionals });
    } else if (route.params?.keyword) {
      return this.searchBannerGQL
        .fetch({
          input: {
            keyword: route.params.keyword,
          },
        })
        .pipe(
          map(r => {
            console.log('[Resolver] Banner fetch result:', r);
            const url = new URL('https://sharry.land' + (r.data.searchBanner?.url || [])[0]);
            const queryParams: Params = {};
            url.searchParams.forEach((value, key) => {
              if (queryParams[key]) {
                if (typeof queryParams[key] === 'string') {
                  queryParams[key] = [queryParams[key], value];
                } else {
                  queryParams[key] = [...queryParams[key], value];
                }
              } else {
                queryParams[key] = value;
              }
            });
            delete queryParams.v;
            queryParams.banner = route.params.keyword;

            return queryParams;
          }),
          catchError(err => {
            console.error('[Resolver] Errore fetch banner:', err);
            return of({});
          }),
        );
    } else {
      return of({});
    }
  }
}

export const searchEngineResolver: ResolveFn<Params> = (route, state) =>
  inject(SearchEngineResolver).resolve(route, state);
